import { useSearchHistory } from './Services';
import type ItinerarySearchForm from '@search/ItinerarySearchForm';
import { TrainSearchForm } from '@search/trip/train/TrainSearchForm';
import { BusSearchForm } from '@search/trip/bus/BusSearchForm';
import { FlightSearchForm } from '@search/trip/flight/FlightSearchForm';
import { HotelSearchForm } from '@search/hotel/HotelSearchForm';
import { type TripSearchForm } from '@search/trip/TripSearchForm';
import type ItineraryPackage from '@itinerary/ItineraryPackage';
import type { PassengerCount } from '@itinerary/types';
import {
    type ItineraryParams,
    type ItineraryPathData,
    ReserveType,
} from '@utils/itinerary';
import { type TripShareLocations } from '@itinerary/trip/TripModuleTypes';
import { type SearchSuggestionItem } from '@search/types';

const ReserveTypes: ReserveType[] = [
    ReserveType.Flight,
    ReserveType.Train,
    ReserveType.Bus,
    ReserveType.Taxi,
    ReserveType.Hotel,
];
const storeKey = 'search-form';
export const useSearchFormStore = defineStore(storeKey, () => {
    const searchHistoryService = useSearchHistory();

    //#region forms
    const flightForm = reactive(new FlightSearchForm()) as FlightSearchForm;
    const hotelForm = reactive(new HotelSearchForm()) as HotelSearchForm;
    const trainForm = reactive(new TrainSearchForm()) as TrainSearchForm;
    const busForm = reactive(new BusSearchForm(null)) as BusSearchForm;
    const taxiForm = reactive(
        new BusSearchForm(8, ReserveType.Taxi, 3)
    ) as BusSearchForm;
    //#endregion
    const urlParams = ref<ItineraryParams>({});
    const currentReserveType = ref<ReserveType | null>(null); // The reserveType currently being viewed
    const firstReserveType = ref<ReserveType | null>(null);
    const needClearPackageSets = ref(false);

    const reserveType = computed<ReserveType>(
        () => currentReserveType.value || ReserveType.Flight
    );

    const allSearchHistory = computed(
        () => searchHistoryService.allSearchHistories
    );
    const currentSearchHistory = computed(
        () => searchHistoryService.allSearchHistories?.[reserveType.value] || []
    );

    const addSearchHistory = () => {
        const data = currentSearchForm().searchHistoryData;
        if (!searchHistoryService?.addSearchHistoryItem || !data) {
            return;
        }
        const { viewUrl } = data;
        const addedBefore = currentSearchHistory.value?.some(
            historyItem => viewUrl === historyItem.viewUrl
        );

        if (addedBefore) {
            return;
        }

        searchHistoryService?.addSearchHistoryItem(data, reserveType.value);
        searchHistoryService.saveSearchHistory();
    };

    // final
    const init = () => {
        searchHistoryService?.init();
    };

    const clearServiceSearchHistory = () => {
        searchHistoryService.clearSearchHistory(reserveType.value);
    };
    const clearAllServiceSearchHistory = () => {
        searchHistoryService.clearAllHistory();
    };

    const targetCode = computed(() => {
        if (reserveType.value === ReserveType.Hotel) {
            const { userDestination } = hotelForm;
            if (userDestination) {
                if (userDestination.type === 'city') {
                    return {
                        destination: userDestination.englishTitle || '',
                    };
                } else {
                    return {
                        destination: userDestination.cityTitle || '',
                        hotelName: userDestination.englishTitle,
                    };
                }
            }
            return { destination: '' };
        }
        return {
            destination:
                currentSearchForm<TripSearchForm>().destinationCode || '',
        };
    });

    const getReserveTypeFromPath = (path: string): ReserveType => {
        if (!path.match(/^\/(flight|train|bus|taxi|hotel|tour)/)) {
            return ReserveType.Flight;
        }
        switch (path.match(/^\/(flight|train|bus|taxi|hotel|tour)/)![1]) {
            case 'flight':
                return ReserveType.Flight;
            case 'train':
                return ReserveType.Train;
            case 'bus':
                return ReserveType.Bus;
            case 'taxi':
                return ReserveType.Taxi;
            case 'hotel':
                return ReserveType.Hotel;
            default:
                throw new Error('reserve type case not handled!');
        }
    };

    const setReserveTypeBaseOnPath = (path: string): void => {
        setReserveType(getReserveTypeFromPath(path));
    };

    const migrateViewPath = (
        path: string,
        pathData: ItineraryPathData
    ): Promise<void> => {
        setReserveTypeBaseOnPath(path);
        return currentSearchForm().migrateViewPath(pathData);
    };

    const verifyViewPath = (
        path: string,
        pathData: ItineraryPathData
    ): Promise<void> => {
        setReserveTypeBaseOnPath(path);
        return currentSearchForm().verifyViewPath(path, pathData);
    };

    const loadViewPath = async (
        path: string,
        pathData: ItineraryPathData
    ): Promise<ItineraryParams> => {
        setReserveTypeBaseOnPath(path);
        const form = currentSearchForm();
        // Parsing url parameters, and loading them
        const parameters = await form.parsePath(pathData);
        await form.loadViewUrlParams(parameters);
        urlParams.value = parameters;
        return parameters;
    };

    const initSearchForm = (viewUrl?: string) => {
        const currentForm = currentSearchForm();
        localStorage.viewUrl = viewUrl || currentForm.viewUrl;
        localStorage.setItem('lastSearch', currentForm.reserveInfo.type);
        currentForm.saveSearch();
    };

    const getFormModule = <T extends ItinerarySearchForm = ItinerarySearchForm>(
        type: ReserveType
    ): T => {
        switch (type) {
            case ReserveType.Train:
                return trainForm as any;
            case ReserveType.Bus:
                return busForm as any;
            case ReserveType.Taxi:
                return taxiForm as any;
            case ReserveType.Hotel:
                return hotelForm as any;
            case ReserveType.Flight:
            default:
                return flightForm as any;
        }
    };

    const setReserveType = (reserveType: ReserveType) => {
        if (
            !ReserveTypes.includes(reserveType) ||
            currentReserveType.value === reserveType
        ) {
            return;
        }
        currentReserveType.value = reserveType;
    };

    const currentSearchForm = <
        T extends ItinerarySearchForm = ItinerarySearchForm,
    >(): T => getFormModule<T>(reserveType.value);

    const reserveInfoTypes = computed(() => {
        if (
            (!firstReserveType.value &&
                reserveType.value === ReserveType.Hotel) ||
            firstReserveType.value === ReserveType.Hotel
        ) {
            return [ReserveType.Hotel];
        }
        return [
            ReserveType.Flight,
            ReserveType.Hotel,
            ReserveType.Train,
            ReserveType.Bus,
            ReserveType.Taxi,
        ];
    });

    /**
     * Loads alternative suggestions, from all reserveType services, except the active one
     */
    const loadSuggestions = (locations: TripShareLocations) => {
        const { routeCountInfo } = currentSearchForm<TripSearchForm>();
        if (routeCountInfo) {
            reserveInfoTypes.value.forEach(reserveInfo => {
                if (reserveInfo === reserveType.value) {
                    return;
                }
                getFormModule(reserveInfo).loadSuggestions({
                    ...routeCountInfo,
                    ...locations,
                });
            });
        }
    };

    const suggestionItems = computed(() =>
        reserveInfoTypes.value.reduce(
            (previousValue, currentValue) => {
                const isCurrent = currentValue === reserveType.value;
                const currentResult =
                    getFormModule(currentValue).suggestionResult;
                return {
                    ...previousValue,
                    [currentValue]: {
                        viewUrl: currentResult?.viewUrl,
                        isLoading: isCurrent
                            ? false
                            : currentResult === undefined,
                        isDisabled: isCurrent ? false : currentResult === null,
                        icon: getFormModule(currentValue).reserveInfo.icon,
                        title: getFormModule(currentValue).reserveInfo.title,
                        isSelected: isCurrent,
                    },
                };
            },
            {} as Record<ReserveType, SearchSuggestionItem>
        )
    );

    const getPassCount = (pkg: ItineraryPackage): PassengerCount => {
        const itiModule = getFormModule(pkg.reserveType);
        return {
            adultCount: itiModule.adultCount,
            childCount: itiModule.childCount,
            infantCount: itiModule.infantCount,
        };
    };

    /**
     * Indicates whether the alternative suggestion bar must be shown in view page, or not
     */
    const haveSuggestions = computed(
        () => !(reserveType.value === 'flight' && !flightForm.isDomestic)
    );

    return {
        needClearPackageSets,
        reserveType,
        _doNotUseCurrentReserveType: currentReserveType,
        firstReserveType,
        trainForm,
        busForm,
        taxiForm,
        flightForm,
        hotelForm,
        urlParams,
        haveSuggestions,
        loadSuggestions,
        suggestionItems,
        targetCode,
        searchHistory: currentSearchHistory,
        allSearchHistory,
        searchHistoryService,
        clearAllServiceSearchHistory,
        init,
        addSearchHistory,
        clearServiceSearchHistory,
        setReserveType,
        currentSearchForm,
        getPassCount,
        getFormModule,
        initSearchForm,
        loadViewPath,
        verifyViewPath,
        migrateViewPath,
        setReserveTypeBaseOnPath,
    };
});

export const useSearchFormStoreIfInstalled = useStoreIfInstalled(
    storeKey,
    useSearchFormStore
);
